"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../hooks/useAuth";
import { commentsAPI, socialUtils, handleApiError, likesAPI } from "../../services/api";
import StarRating from "../social/posts/StarRating";
import { getUserTypeColor } from "../../utils/userTypeUtils";
import { showErrorToast } from "../../utils/notificationUtils";
import { getUserPlaceholderImage } from "../../utils/imageUtils";
import ConfirmModal from "../channels/modals/ConfirmModal";
import ReportModal from "../modals/ReportModal";
import "./ChannelPostComments.css";

// Helper: transform raw API comments to component format
const transformCommentList = (rawComments) => {
  return rawComments
    .map(socialUtils.transformCommentData)
    .filter(comment => comment !== null)
    .map(comment => ({
      id: comment.id,
      content: comment.text,
      author: {
        id: comment.user?.id || comment.user?.user_id,
        name: comment.user.name,
        username: comment.user.name,
        profileImage: comment.user.avatar
      },
      clinic: comment.clinic || null,
      user_id: comment.user?.id || comment.user?.user_id,
      createdAt: comment.timestamp,
      created_at: comment.created_at,
      updated_at: comment.updated_at,
      likes: comment.total_reactions || 0,
      user_reaction_type: comment.user_reaction_type || null,
      reaction_counts: comment.reaction_counts || {},
      total_reactions: comment.total_reactions || 0,
      replies_count: comment.replies_count || 0
    }));
};

const ChannelPostComments = ({ post, onCommentAdded, highlightCommentId }) => {
  const { t } = useTranslation();
  const { getProfileImageUrl, getUserType, user: currentUser } = useAuth();

  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showReactionPicker, setShowReactionPicker] = useState({});
  const [reactingComments, setReactingComments] = useState({});
  const timeoutRefs = useRef({});
  const containerRefs = useRef({});
  const [pickerPositions, setPickerPositions] = useState({});

  // Reply state management
  const [showReplyInput, setShowReplyInput] = useState({});
  const [replyInputs, setReplyInputs] = useState({});
  const [submittingReplies, setSubmittingReplies] = useState({});
  const [replies, setReplies] = useState({});
  const [repliesHasMore, setRepliesHasMore] = useState({});
  const [repliesCurrentPage, setRepliesCurrentPage] = useState({});
  const [showReplies, setShowReplies] = useState({});
  const [loadingReplies, setLoadingReplies] = useState({});

  // Reply reaction state management
  const [showReplyReactionPicker, setShowReplyReactionPicker] = useState({});
  const [reactingReplies, setReactingReplies] = useState({});
  const replyTimeoutRefs = useRef({});
  const replyContainerRefs = useRef({});
  const [replyPickerPositions, setReplyPickerPositions] = useState({});

  // Delete state management
  const [deletingComments, setDeletingComments] = useState({});
  const [deletingReplies, setDeletingReplies] = useState({});

  // Modal state management
  const [deleteCommentModal, setDeleteCommentModal] = useState({ commentId: null, isOpen: false });
  const [deleteReplyModal, setDeleteReplyModal] = useState({ replyId: null, commentId: null, isOpen: false });
  const [errorModal, setErrorModal] = useState({ isOpen: false, message: '' });

  // Action menu state management
  const [openDropdowns, setOpenDropdowns] = useState({});

  // Edit state management
  const [editingComments, setEditingComments] = useState({});
  const [editingReplies, setEditingReplies] = useState({});
  const [editInputs, setEditInputs] = useState({});

  // Report state management
  const [reportTarget, setReportTarget] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);

  // Get current user type color
  const currentUserType = getUserType();
  const userTypeColor = getUserTypeColor(currentUserType);

  // Clinic context: when the post is a clinic post and the viewer owns the clinic,
  // they can comment/reply as the clinic
  const clinicContext = post?.clinic || null;
  const currentUserId = currentUser?.id || currentUser?.user_id;
  const isClinicOwner = !!(
    clinicContext?.id &&
    currentUserId &&
    String(clinicContext.owner_id) === String(currentUserId)
  );
  const [commentAsClinic, setCommentAsClinic] = useState(true);

  // After posting, bring the new item into view. 'nearest' scrolls only as
  // much as needed and is a no-op when the element is already visible, so
  // the surrounding context (post header etc.) is never yanked away.
  const scrollElementIntoView = (elementId) => {
    setTimeout(() => {
      document
        .getElementById(elementId)
        ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 50);
  };

  const [currentHighlightId, setCurrentHighlightId] = useState(null);

  // Infinite scroll pagination state (using refs to avoid stale closures)
  const currentPageRef = useRef(1);
  const hasMoreRef = useRef(true);
  const isLoadingMoreRef = useRef(false);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const loadMoreRef = useRef(null);

  // Load comments (initial or paginated)
  const loadComments = useCallback(async (page = 1, append = false) => {
    if (!post.id) return;
    if (append && isLoadingMoreRef.current) return;

    if (append) {
      isLoadingMoreRef.current = true;
      setIsLoadingMore(true);
    }

    try {
      const response = await commentsAPI.getPostComments(post.id, {
        page,
        perPage: 20
      });

      if (response.data && response.data.data) {
        const transformedComments = transformCommentList(response.data.data);
        const pagination = response.data;
        const moreAvailable = pagination.current_page < pagination.last_page;

        if (append) {
          setComments(prev => [...prev, ...transformedComments]);
        } else {
          setComments(transformedComments);
        }

        hasMoreRef.current = moreAvailable;
        setHasMore(moreAvailable);
      }
    } catch (error) {
      console.error('Error loading comments:', error);
    } finally {
      if (append) {
        isLoadingMoreRef.current = false;
        setIsLoadingMore(false);
      }
    }
  }, [post.id]);

  // Initial load when post changes
  useEffect(() => {
    if (post.id) {
      currentPageRef.current = 1;
      hasMoreRef.current = true;
      isLoadingMoreRef.current = false;
      setHasMore(true);
      loadComments(1, false);
    }
  }, [post.id, loadComments]);

  // Infinite scroll: load more when sentinel enters the scrollable area.
  // The sentinel only renders once the first page of comments exists, so the
  // effect must re-run when comments first load (deps change false -> true).
  const hasLoadedComments = comments.length > 0;
  useEffect(() => {
    const sentinel = loadMoreRef.current;
    if (!sentinel) return;

    // Find the nearest scrollable parent to use as the observer root
    const getScrollableParent = (el) => {
      let parent = el.parentElement;
      while (parent) {
        const style = window.getComputedStyle(parent);
        if (/(auto|scroll)/.test(style.overflowY)) {
          return parent;
        }
        parent = parent.parentElement;
      }
      return null;
    };

    const scrollRoot = getScrollableParent(sentinel);

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMoreRef.current && !isLoadingMoreRef.current) {
          const nextPage = currentPageRef.current + 1;
          currentPageRef.current = nextPage;
          loadComments(nextPage, true);
        }
      },
      { root: scrollRoot, rootMargin: '200px', threshold: 0 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [post.id, loadComments, hasLoadedComments]);

  // Handle comment highlighting and scrolling
  useEffect(() => {
    if (highlightCommentId && comments.length > 0) {
      setCurrentHighlightId(highlightCommentId);

      // Scroll to the comment after a short delay
      const timer = setTimeout(() => {
        const element = document.getElementById(`comment-${highlightCommentId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 500);

      // Clear highlight after 5 seconds
      const clearTimer = setTimeout(() => {
        setCurrentHighlightId(null);
      }, 5000);

      return () => {
        clearTimeout(timer);
        clearTimeout(clearTimer);
      };
    }
  }, [highlightCommentId, comments]);

  const handleSubmitComment = async (e) => {
    e.preventDefault();

    if (!commentText.trim()) return;

    setIsSubmitting(true);

    try {
      // Use existing comments API
      const clinicId = commentAsClinic && isClinicOwner ? clinicContext.id : null;
      const response = await commentsAPI.createComment(post.id, {
        text: commentText,
        ...(clinicId ? { clinic_id: clinicId } : {})
      });

      // Handle different response structures
      const responseData = response.data?.data || response.data || response;

      // Get current user data as fallback - ensure proper URL formatting
      const currentUserName = currentUser
        ? `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim() || currentUser.username || currentUser.name || 'User'
        : 'User';

      // Get profile image URL with proper formatting
      let currentUserImage = getUserPlaceholderImage(currentUser); // Default fallback
      if (currentUser) {
        const profileImageUrl = getProfileImageUrl();
        if (profileImageUrl) {
          currentUserImage = profileImageUrl;
        } else {
          // Try to format the profile_image_url from user object
          const rawImageUrl = currentUser.profile_image_url || currentUser.profileImageUrl || currentUser.avatar || currentUser.profile_image;
          if (rawImageUrl) {
            const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';
            if (rawImageUrl.startsWith('/storage/') || rawImageUrl.startsWith('/uploads/')) {
              currentUserImage = `${API_BASE_URL}${rawImageUrl}`;
            } else if (rawImageUrl.startsWith('http')) {
              currentUserImage = rawImageUrl;
            } else {
              currentUserImage = `${API_BASE_URL}/${rawImageUrl}`;
            }
          }
        }
      }

      if (responseData) {
        const transformedComment = socialUtils.transformCommentData(responseData);

        if (transformedComment && transformedComment.user) {
          // Format the avatar URL if it exists
          let avatarUrl = transformedComment.user.avatar;
          if (avatarUrl) {
            // If it's already a full URL, use it as-is
            if (avatarUrl.startsWith('http')) {
              // Already a full URL, use as-is
            } else if (avatarUrl.startsWith('/storage/') || avatarUrl.startsWith('/uploads/')) {
              // Relative path starting with /storage or /uploads
              const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';
              avatarUrl = `${API_BASE_URL}${avatarUrl}`;
            } else if (!avatarUrl.startsWith('/')) {
              // Relative path without leading slash
              const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';
              avatarUrl = `${API_BASE_URL}/${avatarUrl}`;
            }
            // If it starts with / but not /storage or /uploads, it's likely a local path like /icons/person.png
          } else {
            avatarUrl = currentUserImage;
          }

          const newComment = {
            id: transformedComment.id,
            content: transformedComment.text,
            author: {
              id: currentUser?.id || currentUser?.user_id,
              name: transformedComment.user.name || currentUserName,
              username: transformedComment.user.name || currentUserName,
              profileImage: avatarUrl || currentUserImage
            },
            clinic: transformedComment.clinic || null,
            user_id: currentUser?.id || currentUser?.user_id,
            createdAt: transformedComment.timestamp || "Just now",
            likes: transformedComment.total_reactions || 0,
            user_reaction_type: transformedComment.user_reaction_type || null,
            reaction_counts: transformedComment.reaction_counts || {},
            total_reactions: transformedComment.total_reactions || 0
          };

          setComments(prev => [newComment, ...prev]);
          setCommentText("");
          onCommentAdded(newComment);
          scrollElementIntoView(`comment-${newComment.id}`);
        } else {
          // If transformation fails or user data is missing, create comment with current user data
          const newComment = {
            id: responseData.id || Date.now(),
            content: commentText,
            author: {
              name: currentUserName,
              username: currentUserName,
              profileImage: currentUserImage
            },
            createdAt: "Just now",
            likes: 0,
            user_reaction_type: null,
            reaction_counts: {},
            total_reactions: 0
          };

          setComments(prev => [newComment, ...prev]);
          setCommentText("");
          onCommentAdded(newComment);
          scrollElementIntoView(`comment-${newComment.id}`);
        }
      }
    } catch (error) {
      console.error("Error adding comment:", error);
      setErrorModal({
        isOpen: true,
        message: t(
          "channelPostComments.addError",
          "Failed to add comment. Please try again."
        )
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isArabicText = (text) => {
    if (!text || text.trim() === "") return false;
    const firstChar = text.trim().charAt(0);
    const arabicRegex = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;
    return arabicRegex.test(firstChar);
  };

  const formatContent = (content) => {
    if (!content) return "";

    const isArabic = isArabicText(content);

    // Robustly linkify URLs
    const urlRegex = /(<a\b[^>]*>[\s\S]*?<\/a>)|(<[^>]+>)|(https?:\/\/[^\s<]+)/gi;
    const linkifiedContent = content.replace(urlRegex, (match, aTag, otherTag, url) => {
      if (aTag) return aTag;
      if (otherTag) return otherTag;
      if (url) {
        const displayUrl = socialUtils.truncateUrl(url);
        return `<a href="${url}" target="_blank" rel="noopener noreferrer" style="color: #00D4B3; text-decoration: underline; word-break: break-all;" onClick="event.stopPropagation()">${displayUrl}</a>`;
      }
      return match;
    });

    // Check if originally the content had HTML tags
    const hasHTML = /<[^>]+>/.test(content);

    if (hasHTML) {
      return (
        <div
          style={{
            direction: isArabic ? "rtl" : "ltr",
            textAlign: isArabic ? "right" : "left",
            lineHeight: "1.5",
          }}
          dangerouslySetInnerHTML={{ __html: linkifiedContent }}
        />
      );
    }

    return linkifiedContent.split("\n").map((line, index) => {
      if (line.trim().startsWith("•")) {
        return (
          <div
            key={index}
            style={{
              direction: isArabic ? "rtl" : "ltr",
              textAlign: isArabic ? "right" : "left",
            }}
          >
            <span dangerouslySetInnerHTML={{ __html: line.trim() }} />
          </div>
        );
      }
      return (
        <div
          key={index}
          style={{
            direction: isArabic ? "rtl" : "ltr",
            textAlign: isArabic ? "right" : "left",
            marginBottom: line.trim() ? "4px" : "2px",
          }}
        >
          <span dangerouslySetInnerHTML={{ __html: line || "\u00A0" }} />
        </div>
      );
    });
  };

  // Custom emoji image paths
  const HappyEmoji = "/icons/Reactions/Sulhafa_Emoji_Happy.png";
  const LoveEmoji = "/icons/Reactions/Sulhafa_Emoji_Love.png";
  const LaughEmoji = "/icons/Reactions/Sulhafa_Emoji_Laugh.png";
  const ShockedEmoji = "/icons/Reactions/Sulhafa_Emoji_Shocked.png";
  const SadEmoji = "/icons/Reactions/Sulhafa_Emoji_Sad.png";
  const AngryEmoji = "/icons/Reactions/Sulhafa_Emoji_Angry.png";

  // Reaction types
  const reactions = [LaughEmoji, LoveEmoji, ShockedEmoji, SadEmoji, AngryEmoji, HappyEmoji];
  const reactionTypes = ['funny', 'love', 'wow', 'sad', 'angry', 'like'];
  const reactionTypeToEmoji = {
    'like': HappyEmoji,
    'love': LoveEmoji,
    'funny': LaughEmoji,
    'haha': LaughEmoji,
    'laugh': LaughEmoji,
    'wow': ShockedEmoji,
    'sad': SadEmoji,
    'angry': AngryEmoji
  };
  const emojiToReactionType = {
    [HappyEmoji]: 'like',
    [LoveEmoji]: 'love',
    [LaughEmoji]: 'funny',
    [ShockedEmoji]: 'wow',
    [SadEmoji]: 'sad',
    [AngryEmoji]: 'angry'
  };

  // Helper function to render reaction breakdown
  const renderReactionBreakdown = (reactionCounts) => {
    if (!reactionCounts || typeof reactionCounts !== 'object' || Object.keys(reactionCounts).length === 0) {
      return null;
    }

    // Filter out reactions with 0 count and sort by count (descending)
    const activeReactions = Object.entries(reactionCounts)
      .filter(([type, count]) => count > 0)
      .sort(([, a], [, b]) => b - a);

    if (activeReactions.length === 0) {
      return null;
    }

    return (
      <div className="channel-post-comment-reaction-breakdown">
        <div style={{ display: "flex", alignItems: "center" }}>
          {activeReactions.map(([type, count], index) => {
            const emoji = reactionTypeToEmoji[type] || HappyEmoji;
            const isImagePath = typeof emoji === 'string' && (emoji.startsWith('/') || emoji.includes('.png') || emoji.includes('.jpg') || emoji.includes('.svg'));
            return (
              <span
                key={type}
                className="channel-post-comment-reaction-item"
                title={`${count} ${type} reaction${count !== 1 ? 's' : ''}`}
                style={{ zIndex: 10 - index }}
              >
                <span className="channel-post-comment-reaction-emoji">
                  {isImagePath ? (
                    <img
                      src={emoji}
                      alt={type}
                      onError={(e) => {
                        console.error('Failed to load reaction emoji:', emoji);
                        e.target.style.display = 'none';
                      }}
                      style={{ width: "100%", height: "100%", objectFit: 'contain' }}
                    />
                  ) : (
                    <span>{emoji}</span>
                  )}
                </span>
                <span className="channel-post-comment-reaction-count">{count}</span>
              </span>
            );
          })}
        </div>
        <span className="channel-post-comment-reaction-count">
          {Object.values(reactionCounts).reduce((sum, count) => sum + (count || 0), 0)}
        </span>
      </div>
    );
  };

  // Clear timeout helper
  const clearReactionTimeout = (commentId) => {
    if (timeoutRefs.current[commentId]) {
      window.clearTimeout(timeoutRefs.current[commentId]);
      timeoutRefs.current[commentId] = null;
    }
  };

  // Compute a viewport-fixed position for a reaction picker so it never gets
  // clipped by ancestor containers with overflow: hidden/auto.
  // Opens below the button; flips above only if there isn't room below.
  const computePickerPosition = (containerEl) => {
    if (!containerEl) return null;
    const rect = containerEl.getBoundingClientRect();
    const pickerWidth = 540; // ~6 reactions * 84px + gaps + padding
    const pickerHeight = 110;
    const centerX = Math.min(
      Math.max(rect.left + rect.width / 2, pickerWidth / 2 + 8),
      window.innerWidth - pickerWidth / 2 - 8
    );
    const fitsBelow = window.innerHeight - rect.bottom >= pickerHeight + 16;
    return {
      top: fitsBelow ? rect.bottom + 8 : Math.max(8, rect.top - pickerHeight - 8),
      left: centerX - pickerWidth / 2,
    };
  };

  // Handle mouse enter for reaction picker
  const handleMouseEnter = (commentId) => {
    clearReactionTimeout(commentId);
    // Single-open: when traversing from one reaction to another, close all
    // other pickers immediately so stale ones don't linger behind
    Object.keys(timeoutRefs.current).forEach((key) => {
      if (String(key) !== String(commentId)) clearReactionTimeout(key);
    });
    Object.keys(replyTimeoutRefs.current).forEach(clearReplyReactionTimeout);
    setShowReplyReactionPicker({});
    const pos = computePickerPosition(containerRefs.current[commentId]);
    if (pos) setPickerPositions(prev => ({ ...prev, [commentId]: pos }));
    setShowReactionPicker({ [commentId]: true });
  };

  // Handle mouse leave for reaction picker
  const handleMouseLeave = (commentId, e) => {
    // Don't close if moving to the picker
    const relatedTarget = e?.relatedTarget;
    const container = containerRefs.current[commentId];
    if (relatedTarget && container && container.contains(relatedTarget)) {
      return;
    }
    clearReactionTimeout(commentId);
    timeoutRefs.current[commentId] = window.setTimeout(() => {
      setShowReactionPicker(prev => ({ ...prev, [commentId]: false }));
    }, 500); // Increased delay to give more time to move to picker
  };

  // Handle comment reaction
  const handleCommentReaction = async (commentId, reaction, event) => {
    // Prevent the click from bubbling up
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    if (reactingComments[commentId]) return;

    // Close picker immediately to prevent conflicts
    setShowReactionPicker(prev => ({ ...prev, [commentId]: false }));
    clearReactionTimeout(commentId);

    setReactingComments(prev => ({ ...prev, [commentId]: true }));

    try {
      const comment = comments.find(c => c.id === commentId);
      if (!comment) return;

      const reactionType = emojiToReactionType[reaction] || 'like';
      const currentReaction = comment.user_reaction_type;

      if (currentReaction === reactionType) {
        // Optimistic update: remove reaction immediately
        setComments(prev => prev.map(c => {
          if (c.id !== commentId) return c;
          const currentCounts = { ...(c.reaction_counts || {}) };
          return {
            ...c,
            user_reaction_type: null,
            total_reactions: Math.max(0, (c.total_reactions || 0) - 1),
            likes: Math.max(0, (c.likes || 0) - 1),
            reaction_counts: {
              ...currentCounts,
              [reactionType]: Math.max(0, (currentCounts[reactionType] || 0) - 1)
            }
          };
        }));

        // Remove reaction
        const response = await likesAPI.removeCommentReaction(commentId);

        // Update with server response if available
        if (response && response.data) {
          const responseData = response.data;
          setComments(prev => prev.map(c =>
            c.id === commentId
              ? {
                ...c,
                user_reaction_type: null,
                total_reactions: responseData.reactions_count !== undefined
                  ? responseData.reactions_count
                  : Math.max(0, (c.total_reactions || 0) - 1),
                likes: responseData.reactions_count !== undefined
                  ? responseData.reactions_count
                  : Math.max(0, (c.likes || 0) - 1),
                reaction_counts: responseData.reaction_counts || c.reaction_counts
              }
              : c
          ));
        }
      } else {
        // Add/change reaction
        let response;
        try {
          response = await likesAPI.addCommentReaction(commentId, reactionType);
        } catch (apiError) {
          // Check if error is from notification/broadcasting (non-critical)
          const errorMessage = apiError?.response?.data?.message || apiError?.message || '';
          const errorTrace = JSON.stringify(apiError?.response?.data?.trace || []);
          const isNotificationError = errorMessage.includes('message') ||
            errorMessage.includes('Pusher') ||
            errorMessage.includes('notification') ||
            errorMessage.includes('BroadcastException') ||
            errorTrace.includes('NotificationService') ||
            errorTrace.includes('PusherBroadcaster');

          // Check if error response contains reaction data (reaction was saved despite error)
          const hasReactionData = apiError?.response?.data?.reaction ||
            apiError?.response?.data?.reaction_counts ||
            apiError?.response?.data?.reactions_count !== undefined;

          // If it's a notification/broadcasting error (even 500), the reaction was likely saved
          if (isNotificationError || (apiError?.response?.status === 500 && hasReactionData)) {
            // Use the error response data if it contains reaction info (reaction was saved)
            if (hasReactionData) {
              response = apiError.response; // Reaction was saved, use the data
            } else {
              // No data in error, do optimistic update
              response = null; // Will trigger optimistic update below
            }
            // Silently handle - don't log since reaction worked
          } else if (apiError?.response?.status === 200 || apiError?.response?.status === 201) {
            response = apiError.response; // Use the response even if there were errors
          } else {
            throw apiError; // Re-throw if it's a different error
          }
        }

        // Optimistic update: add/change reaction immediately
        const oldReaction = comment.user_reaction_type;
        const isSwitching = oldReaction && oldReaction !== reactionType;
        const isAdding = !oldReaction;

        setComments(prev => prev.map(c => {
          if (c.id !== commentId) return c;
          const currentCounts = { ...(c.reaction_counts || {}) };
          const updatedCounts = { ...currentCounts };

          // If switching from one reaction to another:
          // 1. Decrement the old reaction count
          // 2. Increment the new reaction count
          // Total reactions count stays the same (switching, not adding)
          if (isSwitching) {
            updatedCounts[oldReaction] = Math.max(0, (updatedCounts[oldReaction] || 0) - 1);
            updatedCounts[reactionType] = (updatedCounts[reactionType] || 0) + 1;
          } else if (isAdding) {
            updatedCounts[reactionType] = (updatedCounts[reactionType] || 0) + 1;
          }

          const currentTotal = c.total_reactions || c.likes || 0;

          return {
            ...c,
            user_reaction_type: reactionType,
            total_reactions: isSwitching ? currentTotal : (isAdding ? currentTotal + 1 : currentTotal),
            likes: isSwitching ? currentTotal : (isAdding ? currentTotal + 1 : currentTotal),
            reaction_counts: updatedCounts
          };
        }));

        // Update with server response if available
        if (response && response.data) {
          const responseData = response.data;
          setComments(prev => prev.map(c => {
            if (c.id !== commentId) return c;

            // Use backend's reaction_counts directly - it should have correct counts
            // (old reaction removed, new reaction added when switching)
            const backendReactionCounts = responseData.reaction_counts;

            // If backend provides reaction_counts, use it; otherwise keep optimistic update
            const finalReactionCounts = backendReactionCounts && Object.keys(backendReactionCounts).length > 0
              ? backendReactionCounts
              : c.reaction_counts; // Keep the optimistic update

            return {
              ...c,
              user_reaction_type: responseData.reaction?.type || reactionType,
              total_reactions: responseData.reactions_count !== undefined
                ? responseData.reactions_count
                : c.total_reactions, // Keep optimistic value if backend doesn't provide
              likes: responseData.reactions_count !== undefined
                ? responseData.reactions_count
                : c.likes, // Keep optimistic value if backend doesn't provide
              reaction_counts: finalReactionCounts
            };
          }));
        }
      }
    } catch (error) {
      // Check if error is from notification/broadcasting (non-critical)
      const errorMessage = error?.response?.data?.message || error?.message || '';
      const errorTrace = JSON.stringify(error?.response?.data?.trace || []);
      const isNotificationError = errorMessage.includes('message') ||
        errorMessage.includes('Pusher') ||
        errorMessage.includes('notification') ||
        errorMessage.includes('BroadcastException') ||
        errorTrace.includes('NotificationService') ||
        errorTrace.includes('PusherBroadcaster');

      // Even if there's an error (like notification service issue),
      // try to update the UI optimistically - reaction was likely saved
      const reactionType = emojiToReactionType[reaction] || 'like';
      const comment = comments.find(c => c.id === commentId);
      if (comment) {
        const currentReaction = comment.user_reaction_type;

        if (currentReaction === reactionType) {
          // Optimistic update for removal
          setComments(prev => prev.map(c =>
            c.id === commentId
              ? {
                ...c,
                user_reaction_type: null,
                total_reactions: Math.max(0, (c.total_reactions || 0) - 1),
                likes: Math.max(0, (c.likes || 0) - 1),
                reaction_counts: {
                  ...c.reaction_counts,
                  [reactionType]: Math.max(0, (c.reaction_counts[reactionType] || 0) - 1)
                }
              }
              : c
          ));
        } else {
          // Optimistic update for addition
          const oldReaction = comment.user_reaction_type;
          const newCounts = { ...comment.reaction_counts };
          if (oldReaction) {
            newCounts[oldReaction] = Math.max(0, (newCounts[oldReaction] || 0) - 1);
          }
          newCounts[reactionType] = (newCounts[reactionType] || 0) + 1;

          setComments(prev => prev.map(c => {
            if (c.id !== commentId) return c;
            return {
              ...c,
              user_reaction_type: reactionType,
              total_reactions: (c.total_reactions || 0) + (oldReaction ? 0 : 1),
              likes: (c.likes || 0) + (oldReaction ? 0 : 1),
              reaction_counts: newCounts
            };
          }));
        }
      }

      // Only log unexpected errors - notification errors are expected and reaction worked
      if (!isNotificationError) {
        console.error("Error reacting to comment:", error);
      }
      // Silently handle notification errors since reaction was saved successfully
    } finally {
      setReactingComments(prev => ({ ...prev, [commentId]: false }));
    }
  };

  // Load replies for a comment
  const loadReplies = async (commentId, page = 1) => {
    if (loadingReplies[commentId]) return;

    setLoadingReplies(prev => ({ ...prev, [commentId]: true }));

    try {
      const response = await commentsAPI.getCommentReplies(post.id, commentId, {
        page,
        perPage: 10
      });

      const payload = response.data || {};
      const repliesData = payload.data || [];
      const hasMore = payload.next_page_url != null;
      const lastPage = payload.last_page || page;

      const transformedReplies = repliesData
        .map(socialUtils.transformCommentData)
        .filter(reply => reply !== null)
        .map(reply => ({
          id: reply.id,
          content: reply.text,
          author: {
            id: reply.user?.id || reply.user?.user_id,
            name: reply.user.name,
            username: reply.user.name,
            profileImage: reply.user.avatar
          },
          clinic: reply.clinic || null,
          user_id: reply.user?.id || reply.user?.user_id,
          createdAt: reply.timestamp,
          created_at: reply.created_at,
          updated_at: reply.updated_at,
          likes: reply.total_reactions || 0,
          user_reaction_type: reply.user_reaction_type || null,
          reaction_counts: reply.reaction_counts || {},
          total_reactions: reply.total_reactions || 0,
          parent_id: commentId
        }));

      setReplies(prev => ({
        ...prev,
        [commentId]: page > 1
          ? [...(prev[commentId] || []), ...transformedReplies]
          : transformedReplies
      }));
      setRepliesCurrentPage(prev => ({ ...prev, [commentId]: page }));
      setRepliesHasMore(prev => ({ ...prev, [commentId]: hasMore || page < lastPage }));
    } catch (error) {
      console.error('Error loading replies:', error);
      if (page === 1) {
        setReplies(prev => ({ ...prev, [commentId]: [] }));
      }
    } finally {
      setLoadingReplies(prev => ({ ...prev, [commentId]: false }));
    }
  };

  // Load the next page of replies for a comment (pagination)
  const handleLoadMoreReplies = (commentId) => {
    const nextPage = (repliesCurrentPage[commentId] || 1) + 1;
    loadReplies(commentId, nextPage);
  };


  // Toggle reply input visibility
  const toggleReplyInput = (commentId) => {
    setShowReplyInput(prev => ({ ...prev, [commentId]: !prev[commentId] }));
    if (!showReplyInput[commentId]) {
      setReplyInputs(prev => ({ ...prev, [commentId]: '' }));
    }
  };

  // Toggle replies visibility
  const toggleReplies = (commentId) => {
    if (!showReplies[commentId] && !replies[commentId]) {
      loadReplies(commentId);
    }
    const newShowReplies = !showReplies[commentId];
    setShowReplies(prev => ({ ...prev, [commentId]: newShowReplies }));
    // Automatically show reply input when showing replies
    if (newShowReplies) {
      setShowReplyInput(prev => ({ ...prev, [commentId]: true }));
    }
  };

  // Clear timeout helper for replies
  const clearReplyReactionTimeout = (replyId) => {
    if (replyTimeoutRefs.current[replyId]) {
      window.clearTimeout(replyTimeoutRefs.current[replyId]);
      replyTimeoutRefs.current[replyId] = null;
    }
  };

  // Handle mouse enter for reply reaction picker
  const handleReplyMouseEnter = (replyId) => {
    clearReplyReactionTimeout(replyId);
    // Single-open: close comment pickers and other reply pickers immediately
    Object.keys(replyTimeoutRefs.current).forEach((key) => {
      if (String(key) !== String(replyId)) clearReplyReactionTimeout(key);
    });
    Object.keys(timeoutRefs.current).forEach(clearReactionTimeout);
    setShowReactionPicker({});
    const pos = computePickerPosition(replyContainerRefs.current[replyId]);
    if (pos) setReplyPickerPositions(prev => ({ ...prev, [replyId]: pos }));
    setShowReplyReactionPicker({ [replyId]: true });
  };

  // Handle mouse leave for reply reaction picker
  const handleReplyMouseLeave = (replyId, e) => {
    const relatedTarget = e?.relatedTarget;
    const container = replyContainerRefs.current[replyId];
    if (relatedTarget && container && container.contains(relatedTarget)) {
      return;
    }
    clearReplyReactionTimeout(replyId);
    replyTimeoutRefs.current[replyId] = window.setTimeout(() => {
      setShowReplyReactionPicker(prev => ({ ...prev, [replyId]: false }));
    }, 500);
  };

  // Handle reply reaction
  const handleReplyReaction = async (replyId, reaction, event, commentId) => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    if (reactingReplies[replyId]) return;

    setShowReplyReactionPicker(prev => ({ ...prev, [replyId]: false }));
    clearReplyReactionTimeout(replyId);

    setReactingReplies(prev => ({ ...prev, [replyId]: true }));

    try {
      const reply = replies[commentId]?.find(r => r.id === replyId);
      if (!reply) return;

      const reactionType = emojiToReactionType[reaction] || 'like';
      const currentReaction = reply.user_reaction_type;

      if (currentReaction === reactionType) {
        // Remove reaction
        setReplies(prev => ({
          ...prev,
          [commentId]: prev[commentId].map(r => {
            if (r.id !== replyId) return r;
            const currentCounts = { ...(r.reaction_counts || {}) };
            return {
              ...r,
              user_reaction_type: null,
              total_reactions: Math.max(0, (r.total_reactions || 0) - 1),
              likes: Math.max(0, (r.likes || 0) - 1),
              reaction_counts: {
                ...currentCounts,
                [reactionType]: Math.max(0, (currentCounts[reactionType] || 0) - 1)
              }
            };
          })
        }));

        const response = await likesAPI.removeCommentReaction(replyId);

        if (response && response.data) {
          const responseData = response.data;
          setReplies(prev => ({
            ...prev,
            [commentId]: prev[commentId].map(r =>
              r.id === replyId
                ? {
                  ...r,
                  user_reaction_type: null,
                  total_reactions: responseData.reactions_count !== undefined
                    ? responseData.reactions_count
                    : Math.max(0, (r.total_reactions || 0) - 1),
                  likes: responseData.reactions_count !== undefined
                    ? responseData.reactions_count
                    : Math.max(0, (r.likes || 0) - 1),
                  reaction_counts: responseData.reaction_counts || r.reaction_counts
                }
                : r
            )
          }));
        }
      } else {
        // Add/change reaction
        let response;
        try {
          response = await likesAPI.addCommentReaction(replyId, reactionType);
        } catch (apiError) {
          const errorMessage = apiError?.response?.data?.message || apiError?.message || '';
          const errorTrace = JSON.stringify(apiError?.response?.data?.trace || []);
          const isNotificationError = errorMessage.includes('message') ||
            errorMessage.includes('Pusher') ||
            errorMessage.includes('notification') ||
            errorMessage.includes('BroadcastException') ||
            errorTrace.includes('NotificationService') ||
            errorTrace.includes('PusherBroadcaster');

          const hasReactionData = apiError?.response?.data?.reaction ||
            apiError?.response?.data?.reaction_counts ||
            apiError?.response?.data?.reactions_count !== undefined;

          if (isNotificationError || (apiError?.response?.status === 500 && hasReactionData)) {
            if (hasReactionData) {
              response = apiError.response;
            } else {
              response = null;
            }
          } else if (apiError?.response?.status === 200 || apiError?.response?.status === 201) {
            response = apiError.response;
          } else {
            throw apiError;
          }
        }

        // Optimistic update
        const oldReaction = reply.user_reaction_type;
        const isSwitching = oldReaction && oldReaction !== reactionType;
        const isAdding = !oldReaction;

        setReplies(prev => ({
          ...prev,
          [commentId]: prev[commentId].map(r => {
            if (r.id !== replyId) return r;
            const currentCounts = { ...(r.reaction_counts || {}) };
            const updatedCounts = { ...currentCounts };

            if (isSwitching) {
              updatedCounts[oldReaction] = Math.max(0, (updatedCounts[oldReaction] || 0) - 1);
              updatedCounts[reactionType] = (updatedCounts[reactionType] || 0) + 1;
            } else if (isAdding) {
              updatedCounts[reactionType] = (updatedCounts[reactionType] || 0) + 1;
            }

            const currentTotal = r.total_reactions || r.likes || 0;

            return {
              ...r,
              user_reaction_type: reactionType,
              total_reactions: isSwitching ? currentTotal : (isAdding ? currentTotal + 1 : currentTotal),
              likes: isSwitching ? currentTotal : (isAdding ? currentTotal + 1 : currentTotal),
              reaction_counts: updatedCounts
            };
          })
        }));

        if (response && response.data) {
          const responseData = response.data;
          setReplies(prev => ({
            ...prev,
            [commentId]: prev[commentId].map(r => {
              if (r.id !== replyId) return r;

              const backendReactionCounts = responseData.reaction_counts;
              const finalReactionCounts = backendReactionCounts && Object.keys(backendReactionCounts).length > 0
                ? backendReactionCounts
                : r.reaction_counts;

              return {
                ...r,
                user_reaction_type: responseData.reaction?.type || reactionType,
                total_reactions: responseData.reactions_count !== undefined
                  ? responseData.reactions_count
                  : r.total_reactions,
                likes: responseData.reactions_count !== undefined
                  ? responseData.reactions_count
                  : r.likes,
                reaction_counts: finalReactionCounts
              };
            })
          }));
        }
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message || error?.message || '';
      const errorTrace = JSON.stringify(error?.response?.data?.trace || []);
      const isNotificationError = errorMessage.includes('message') ||
        errorMessage.includes('Pusher') ||
        errorMessage.includes('notification') ||
        errorMessage.includes('BroadcastException') ||
        errorTrace.includes('NotificationService') ||
        errorTrace.includes('PusherBroadcaster');

      if (!isNotificationError) {
        console.error("Error reacting to reply:", error);
      }
    } finally {
      setReactingReplies(prev => ({ ...prev, [replyId]: false }));
    }
  };

  // Check if current user owns a comment/reply
  const isCommentOwner = (comment) => {
    if (!currentUser) return false;
    const currentUserId = currentUser.id || currentUser.user_id;
    // Check various possible locations for the comment author's user ID
    // The comment might have author.id, user_id, or user.id depending on transformation
    const commentUserId = comment.author?.id ||
      comment.user_id ||
      comment.user?.id ||
      comment.author?.user_id;
    return currentUserId === commentUserId;
  };

  // Check if a comment/reply has been edited (updated_at > created_at by more than 5s)
  const isEdited = (item) => {
    if (!item.created_at || !item.updated_at) return false;
    const created = new Date(item.created_at).getTime();
    const updated = new Date(item.updated_at).getTime();
    return !isNaN(created) && !isNaN(updated) && (updated - created) > 5000;
  };

  // Toggle action dropdown for a comment or reply
  const toggleDropdown = (itemKey, e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    setOpenDropdowns(prev => ({
      ...prev,
      [itemKey]: !prev[itemKey]
    }));
  };

  const closeAllDropdowns = () => {
    setOpenDropdowns({});
  };

  // Start editing a comment
  const startEditComment = (comment) => {
    const itemKey = `comment-${comment.id}`;
    setEditInputs(prev => ({ ...prev, [itemKey]: comment.content || '' }));
    setEditingComments(prev => ({ ...prev, [comment.id]: true }));
    closeAllDropdowns();
  };

  // Start editing a reply
  const startEditReply = (reply) => {
    const itemKey = `reply-${reply.id}`;
    setEditInputs(prev => ({ ...prev, [itemKey]: reply.content || '' }));
    setEditingReplies(prev => ({ ...prev, [reply.id]: true }));
    closeAllDropdowns();
  };

  // Cancel editing
  const cancelEdit = (itemKey) => {
    if (itemKey.startsWith('comment-')) {
      const commentId = parseInt(itemKey.replace('comment-', ''), 10);
      setEditingComments(prev => ({ ...prev, [commentId]: false }));
    } else if (itemKey.startsWith('reply-')) {
      const replyId = parseInt(itemKey.replace('reply-', ''), 10);
      setEditingReplies(prev => ({ ...prev, [replyId]: false }));
    }
    setEditInputs(prev => ({ ...prev, [itemKey]: '' }));
  };

  // Save edited comment or reply
  const saveEdit = async (itemKey) => {
    const newText = editInputs[itemKey]?.trim();
    if (!newText) return;

    if (itemKey.startsWith('comment-')) {
      const commentId = parseInt(itemKey.replace('comment-', ''), 10);
      const comment = comments.find(c => c.id === commentId);
      if (!comment) return;

      // Optimistic update
      setComments(prev => prev.map(c =>
        c.id === commentId ? { ...c, content: newText } : c
      ));
      setEditingComments(prev => ({ ...prev, [commentId]: false }));

      try {
        const response = await commentsAPI.updateComment(post.id, commentId, { text: newText });
        const responseData = response.data?.data || response.data || response;
        const updatedAt = responseData?.updated_at || responseData?.comment?.updated_at;
        if (updatedAt) {
          setComments(prev => prev.map(c =>
            c.id === commentId ? { ...c, updated_at: updatedAt } : c
          ));
        }
      } catch (error) {
        // Revert on error
        setComments(prev => prev.map(c =>
          c.id === commentId ? { ...c, content: comment.content } : c
        ));
        console.error("Error updating comment:", error);
        setErrorModal({
          isOpen: true,
          message: t("channelPostComments.editError", "Failed to update comment. Please try again.")
        });
      }
    } else if (itemKey.startsWith('reply-')) {
      const replyId = parseInt(itemKey.replace('reply-', ''), 10);
      // Find the parent comment and the reply
      let parentCommentId = null;
      let reply = null;
      Object.entries(replies).forEach(([commentId, replyList]) => {
        const found = replyList.find(r => r.id === replyId);
        if (found) {
          parentCommentId = parseInt(commentId, 10);
          reply = found;
        }
      });
      if (!reply || !parentCommentId) return;

      // Optimistic update
      setReplies(prev => ({
        ...prev,
        [parentCommentId]: prev[parentCommentId].map(r =>
          r.id === replyId ? { ...r, content: newText } : r
        )
      }));
      setEditingReplies(prev => ({ ...prev, [replyId]: false }));

      try {
        const response = await commentsAPI.updateComment(post.id, replyId, { text: newText });
        const responseData = response.data?.data || response.data || response;
        const updatedAt = responseData?.updated_at || responseData?.comment?.updated_at;
        if (updatedAt) {
          setReplies(prev => ({
            ...prev,
            [parentCommentId]: prev[parentCommentId].map(r =>
              r.id === replyId ? { ...r, updated_at: updatedAt } : r
            )
          }));
        }
      } catch (error) {
        // Revert on error
        setReplies(prev => ({
          ...prev,
          [parentCommentId]: prev[parentCommentId].map(r =>
            r.id === replyId ? { ...r, content: reply.content } : r
          )
        }));
        console.error("Error updating reply:", error);
        setErrorModal({
          isOpen: true,
          message: t("channelPostComments.editError", "Failed to update reply. Please try again.")
        });
      }
    }
  };

  // Open report modal for a comment or reply
  const handleReportClick = (item) => {
    setReportTarget({
      id: item.id,
      type: 'App\\Models\\PostComment',
      authorName: item.author?.name
    });
    setShowReportModal(true);
    closeAllDropdowns();
  };

  const handleReportSuccess = () => {
    if (reportTarget) {
      // Mark the reported item locally to avoid re-reporting
      setComments(prev => prev.map(c =>
        c.id === reportTarget.id ? { ...c, has_reported: true } : c
      ));
      setReplies(prev => {
        const next = { ...prev };
        Object.keys(next).forEach(commentId => {
          next[commentId] = next[commentId].map(r =>
            r.id === reportTarget.id ? { ...r, has_reported: true } : r
          );
        });
        return next;
      });
    }
  };

  // Handle comment deletion
  const handleDeleteCommentClick = (commentId) => {
    setDeleteCommentModal({ commentId, isOpen: true });
  };

  const handleDeleteCommentConfirm = async () => {
    const commentId = deleteCommentModal.commentId;
    if (!commentId) return;

    setDeleteCommentModal({ commentId: null, isOpen: false });
    setDeletingComments(prev => ({ ...prev, [commentId]: true }));

    try {
      await commentsAPI.deleteComment(post.id, commentId);

      // Remove comment from state
      setComments(prev => prev.filter(c => c.id !== commentId));

      // Update comment count if callback provided
      if (onCommentAdded) {
        // This is a bit of a hack - we'll use the callback to notify parent
        // that a comment was removed (negative increment)
      }
    } catch (error) {
      console.error("Error deleting comment:", error);
      setErrorModal({
        isOpen: true,
        message: t(
          "channelPostComments.deleteError",
          "Failed to delete comment. Please try again."
        )
      });
    } finally {
      setDeletingComments(prev => ({ ...prev, [commentId]: false }));
    }
  };

  const handleDeleteCommentCancel = () => {
    setDeleteCommentModal({ commentId: null, isOpen: false });
  };

  // Handle reply deletion
  const handleDeleteReplyClick = (replyId, commentId) => {
    setDeleteReplyModal({ replyId, commentId, isOpen: true });
  };

  const handleDeleteReplyConfirm = async () => {
    const { replyId, commentId } = deleteReplyModal;
    if (!replyId || !commentId) return;

    setDeleteReplyModal({ replyId: null, commentId: null, isOpen: false });
    setDeletingReplies(prev => ({ ...prev, [replyId]: true }));

    try {
      await commentsAPI.deleteComment(post.id, replyId);

      // Remove reply from state
      setReplies(prev => ({
        ...prev,
        [commentId]: (prev[commentId] || []).filter(r => r.id !== replyId)
      }));

      // Update parent comment's replies_count
      setComments(prev => prev.map(c =>
        c.id === commentId
          ? { ...c, replies_count: Math.max(0, (c.replies_count || 0) - 1) }
          : c
      ));
    } catch (error) {
      console.error("Error deleting reply:", error);
      setErrorModal({
        isOpen: true,
        message: t(
          "channelPostComments.deleteError",
          "Failed to delete reply. Please try again."
        )
      });
    } finally {
      setDeletingReplies(prev => ({ ...prev, [replyId]: false }));
    }
  };

  const handleDeleteReplyCancel = () => {
    setDeleteReplyModal({ replyId: null, commentId: null, isOpen: false });
  };

  // Handle reply submission
  const handleReplySubmit = async (commentId, e) => {
    e.preventDefault();

    const replyText = replyInputs[commentId]?.trim();
    if (!replyText) return;

    setSubmittingReplies(prev => ({ ...prev, [commentId]: true }));

    try {
      const replyClinicId = commentAsClinic && isClinicOwner ? clinicContext.id : null;
      const response = await commentsAPI.createReply(post.id, commentId, {
        text: replyText,
        ...(replyClinicId ? { clinic_id: replyClinicId } : {})
      });

      const responseData = response.data?.data || response.data || response;

      // Get current user data
      const currentUserName = currentUser
        ? `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim() || currentUser.username || currentUser.name || 'User'
        : 'User';

      let currentUserImage = getUserPlaceholderImage(currentUser);
      if (currentUser) {
        const profileImageUrl = getProfileImageUrl();
        if (profileImageUrl) {
          currentUserImage = profileImageUrl;
        } else {
          const rawImageUrl = currentUser.profile_image_url || currentUser.profileImageUrl || currentUser.avatar || currentUser.profile_image;
          if (rawImageUrl) {
            const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';
            if (rawImageUrl.startsWith('/storage/') || rawImageUrl.startsWith('/uploads/')) {
              currentUserImage = `${API_BASE_URL}${rawImageUrl}`;
            } else if (rawImageUrl.startsWith('http')) {
              currentUserImage = rawImageUrl;
            } else {
              currentUserImage = `${API_BASE_URL}/${rawImageUrl}`;
            }
          }
        }
      }

      if (responseData) {
        const transformedReply = socialUtils.transformCommentData(responseData);

        const newReply = {
          id: transformedReply?.id || responseData.id || Date.now(),
          content: transformedReply?.text || responseData.text || replyText,
          author: {
            id: currentUser?.id || currentUser?.user_id,
            name: transformedReply?.user?.name || currentUserName,
            username: transformedReply?.user?.name || currentUserName,
            profileImage: transformedReply?.user?.avatar || currentUserImage
          },
          clinic: transformedReply?.clinic || null,
          user_id: currentUser?.id || currentUser?.user_id,
          createdAt: transformedReply?.timestamp || "Just now",
          likes: transformedReply?.total_reactions || 0,
          user_reaction_type: transformedReply?.user_reaction_type || null,
          reaction_counts: transformedReply?.reaction_counts || {},
          total_reactions: transformedReply?.total_reactions || 0,
          parent_id: commentId
        };

        setReplies(prev => ({
          ...prev,
          [commentId]: [newReply, ...(prev[commentId] || [])]
        }));

        // Update comment's replies_count
        setComments(prev => prev.map(c =>
          c.id === commentId
            ? { ...c, replies_count: (c.replies_count || 0) + 1 }
            : c
        ));

        setReplyInputs(prev => ({ ...prev, [commentId]: '' }));
        scrollElementIntoView(`reply-${newReply.id}`);
      }
    } catch (error) {
      console.error("Error adding reply:", error);
      setErrorModal({
        isOpen: true,
        message: t(
          "channelPostComments.replyError",
          "Failed to add reply. Please try again."
        )
      });
    } finally {
      setSubmittingReplies(prev => ({ ...prev, [commentId]: false }));
    }
  };

  // Close reaction picker and action dropdowns when clicking outside
  const dropdownRefs = useRef({});

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Handle comment reaction pickers
      Object.keys(containerRefs.current).forEach(commentId => {
        const container = containerRefs.current[commentId];
        if (container && !container.contains(event.target)) {
          setShowReactionPicker(prev => ({ ...prev, [commentId]: false }));
          clearReactionTimeout(commentId);
        }
      });

      // Handle reply reaction pickers
      Object.keys(replyContainerRefs.current).forEach(replyId => {
        const container = replyContainerRefs.current[replyId];
        if (container && !container.contains(event.target)) {
          setShowReplyReactionPicker(prev => ({ ...prev, [replyId]: false }));
          clearReplyReactionTimeout(replyId);
        }
      });

      // Handle action dropdowns
      Object.keys(dropdownRefs.current).forEach(itemKey => {
        const container = dropdownRefs.current[itemKey];
        if (container && !container.contains(event.target)) {
          setOpenDropdowns(prev => ({ ...prev, [itemKey]: false }));
        }
      });
    };

    document.addEventListener('click', handleClickOutside, true);

    // Close pickers on scroll so viewport-fixed popups don't detach from their buttons
    const handleScroll = () => {
      setShowReactionPicker({});
      setShowReplyReactionPicker({});
    };
    window.addEventListener('scroll', handleScroll, { passive: true, capture: true });

    return () => {
      document.removeEventListener('click', handleClickOutside, true);
      window.removeEventListener('scroll', handleScroll, { capture: true });
    };
  }, []);

  return (
    <div className="channel-post-comments" style={{ '--user-type-color': userTypeColor }}>
      <div className="channel-post-comments-list">
        {comments.length > 0 ? (
          <>
            {comments.map(comment => (
              <div
                key={comment.id}
                id={`comment-${comment.id}`}
                className={`channel-post-comment ${currentHighlightId === comment.id ? 'channel-post-comment-highlighted' : ''}`}
              >
              <img
                src={comment.clinic
                  ? comment.clinic.logo || getUserPlaceholderImage()
                  : comment.author.profileImage || getUserPlaceholderImage(comment.author)}
                alt={comment.clinic
                  ? comment.clinic.name_en || comment.clinic.name_ar
                  : comment.author.name}
                className="channel-post-comment-avatar"
                onError={(e) => {
                  e.target.src = getUserPlaceholderImage(comment.author);
                }}
              />
              <div className="channel-post-comment-content">
                <div className="channel-post-comment-header">
                  <div className="channel-post-comment-header-info">
                    <span
                      className={`channel-post-comment-author${comment.clinic?.id ? " channel-post-comment-author--clinic" : ""}`}
                      onClick={(e) => {
                        if (!comment.clinic?.id) return;
                        e.stopPropagation();
                        window.location.href = `/clinic/${comment.clinic.id}`;
                      }}
                    >
                      {comment.clinic
                        ? comment.clinic.name_en || comment.clinic.name_ar
                        : comment.author.name}
                    </span>
                    <span className="channel-post-comment-time">
                      {comment.createdAt}
                      {isEdited(comment) && (
                        <span className="channel-post-comment-edited"> · {t("channelPostComments.edited", "Edited")}</span>
                      )}
                    </span>
                  </div>

                  {/* Action Menu */}
                  {currentUser && (currentUser.id || currentUser.user_id) && (
                    <div
                      className="channel-post-comment-menu"
                      ref={(el) => dropdownRefs.current[`comment-${comment.id}`] = el}
                    >
                      <button
                        className="channel-post-comment-menu-btn"
                        onClick={(e) => toggleDropdown(`comment-${comment.id}`, e)}
                        aria-label={t("channelPostComments.actions", "Actions")}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
                        </svg>
                      </button>

                      {openDropdowns[`comment-${comment.id}`] && (
                        <div className="channel-post-comment-dropdown">
                          {isCommentOwner(comment) ? (
                            <>
                              <button
                                className="channel-post-comment-dropdown-item"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  startEditComment(comment);
                                }}
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                </svg>
                                <span>{t("channelPostComments.edit", "Edit")}</span>
                              </button>
                              <button
                                className="channel-post-comment-dropdown-item channel-post-comment-dropdown-item--delete"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteCommentClick(comment.id);
                                  closeAllDropdowns();
                                }}
                                disabled={deletingComments[comment.id]}
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                </svg>
                                <span>{t("channelPostComments.delete", "Delete")}</span>
                              </button>
                            </>
                          ) : (
                            <button
                              className="channel-post-comment-dropdown-item"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleReportClick(comment);
                              }}
                              disabled={comment.has_reported}
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"></path>
                                <line x1="4" y1="22" x2="4" y2="15"></line>
                              </svg>
                              <span>
                                {comment.has_reported
                                  ? t("channelPostComments.alreadyReported", "Already Reported")
                                  : t("channelPostComments.report", "Report")}
                              </span>
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {editingComments[comment.id] ? (
                  <div className="channel-post-comment-edit-form">
                    <textarea
                      value={editInputs[`comment-${comment.id}`] || ''}
                      onChange={(e) => setEditInputs(prev => ({ ...prev, [`comment-${comment.id}`]: e.target.value }))}
                      placeholder={t("channelPostComments.editPlaceholder", "Edit your comment...")}
                      className="channel-post-comment-edit-input"
                      dir={isArabicText(editInputs[`comment-${comment.id}`] || '') ? 'rtl' : 'ltr'}
                      rows={2}
                    />
                    <div className="channel-post-comment-edit-actions">
                      <button
                        className="channel-post-comment-edit-btn channel-post-comment-edit-btn--cancel"
                        onClick={() => cancelEdit(`comment-${comment.id}`)}
                      >
                        {t("channelPostComments.cancel", "Cancel")}
                      </button>
                      <button
                        className="channel-post-comment-edit-btn channel-post-comment-edit-btn--save"
                        onClick={() => saveEdit(`comment-${comment.id}`)}
                        disabled={!editInputs[`comment-${comment.id}`]?.trim()}
                      >
                        {t("channelPostComments.save", "Save")}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div
                    className="channel-post-comment-text"
                    dir={isArabicText(comment.content) ? 'rtl' : 'ltr'}
                  >
                    {formatContent(comment.content)}
                  </div>
                )}
                <div className="channel-post-comment-actions">
                  <div className="channel-post-comment-actions-wrapper">
                    <div
                      ref={(el) => containerRefs.current[comment.id] = el}
                      className="channel-post-comment-reaction-container"
                      onMouseEnter={() => handleMouseEnter(comment.id)}
                      onMouseLeave={(e) => handleMouseLeave(comment.id, e)}
                      style={{ position: 'relative' }}
                    >
                      <button
                        className={`channel-post-comment-like-btn ${comment.user_reaction_type ? 'has-reaction' : ''}`}
                        onClick={(e) => {
                          e.preventDefault();
                          if (!showReactionPicker[comment.id]) {
                            handleMouseEnter(comment.id);
                          }
                        }}
                      >
                        {comment.user_reaction_type && reactionTypeToEmoji[comment.user_reaction_type] ? (
                          (() => {
                            const emoji = reactionTypeToEmoji[comment.user_reaction_type];
                            const isImagePath = typeof emoji === 'string' && (emoji.startsWith('/') || emoji.includes('.png') || emoji.includes('.jpg') || emoji.includes('.svg'));
                            return isImagePath ? (
                              <img
                                src={emoji}
                                alt={comment.user_reaction_type}
                                style={{ width: "32px", height: "32px", objectFit: 'contain', display: 'inline-block' }}
                              />
                            ) : (
                              <span style={{ fontSize: '16px' }}>{emoji}</span>
                            );
                          })()
                        ) : (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
                          </svg>
                        )}
                        <span>{comment.total_reactions || comment.likes || 0}</span>
                      </button>

                      {showReactionPicker[comment.id] && (
                        <div
                          className="channel-post-reaction-picker"
                          style={{
                            position: 'fixed',
                            top: `${pickerPositions[comment.id]?.top ?? 0}px`,
                            left: `${pickerPositions[comment.id]?.left ?? 0}px`,
                            bottom: 'auto',
                          }}
                          onMouseEnter={() => handleMouseEnter(comment.id)}
                          onMouseLeave={(e) => handleMouseLeave(comment.id, e)}
                        >
                          {reactions.map((reaction, index) => (
                            <button
                              key={index}
                              className="channel-post-reaction-emoji"
                              onMouseDown={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                handleCommentReaction(comment.id, reaction, e);
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                              }}
                              disabled={reactingComments[comment.id]}
                            >
                              {(() => {
                                const isImagePath = typeof reaction === 'string' && (reaction.startsWith('/') || reaction.includes('.png') || reaction.includes('.jpg') || reaction.includes('.svg'));
                                return isImagePath ? (
                                  <img
                                    src={reaction}
                                    alt={reactionTypes[index]}
                                    style={{ width: "72px", height: "72px", objectFit: 'contain', display: 'inline-block' }}
                                  />
                                ) : (
                                  <span>{reaction}</span>
                                );
                              })()}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    {renderReactionBreakdown(comment.reaction_counts)}

                    {/* Reply Button */}
                    <button
                      className="channel-post-comment-reply-btn"
                      onClick={(e) => {
                        e.preventDefault();
                        toggleReplyInput(comment.id);
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                      </svg>
                      <span>{t("channelPostComments.reply", "Reply")}</span>
                    </button>

                    {/* Show Replies Button */}
                    {((comment.replies_count > 0) || (replies[comment.id] && replies[comment.id].length > 0)) && (
                      <button
                        className="channel-post-comment-reply-btn"
                        onClick={(e) => {
                          e.preventDefault();
                          toggleReplies(comment.id);
                        }}
                      >
                        {showReplies[comment.id]
                          ? t("channelPostComments.hideReplies", "Hide replies")
                          : t("channelPostComments.showReplies", `Show ${replies[comment.id]?.length || comment.replies_count || 0} ${(replies[comment.id]?.length || comment.replies_count || 0) === 1 ? 'reply' : 'replies'}`)}
                      </button>
                    )}
                  </div>
                </div>

                {/* Replies Section */}
                {showReplies[comment.id] && replies[comment.id] && replies[comment.id].length > 0 && (
                  <div className="channel-post-replies-section">
                    {loadingReplies[comment.id] ? (
                      <div className="channel-post-replies-loading">
                        {t("channelPostComments.loading", "Loading...")}
                      </div>
                    ) : (
                      replies[comment.id].map((reply) => (
                        <div key={reply.id} id={`reply-${reply.id}`} className="channel-post-reply">
                          <img
                            src={reply.clinic
                              ? reply.clinic.logo || getUserPlaceholderImage()
                              : reply.author.profileImage || getUserPlaceholderImage(reply.author)}
                            alt={reply.clinic
                              ? reply.clinic.name_en || reply.clinic.name_ar
                              : reply.author.name}
                            className="channel-post-reply-avatar"
                            onError={(e) => {
                              e.target.src = getUserPlaceholderImage(comment.author);
                            }}
                          />
                          <div className="channel-post-reply-content">
                            <div className="channel-post-reply-header">
                              <div className="channel-post-reply-header-info">
                                <span
                                  className={`channel-post-reply-author${reply.clinic?.id ? " channel-post-reply-author--clinic" : ""}`}
                                  onClick={(e) => {
                                    if (!reply.clinic?.id) return;
                                    e.stopPropagation();
                                    window.location.href = `/clinic/${reply.clinic.id}`;
                                  }}
                                >
                                  {reply.clinic
                                    ? reply.clinic.name_en || reply.clinic.name_ar
                                    : reply.author.name}
                                </span>
                                <span className="channel-post-reply-time">
                                  {reply.createdAt}
                                  {isEdited(reply) && (
                                    <span className="channel-post-reply-edited"> · {t("channelPostComments.edited", "Edited")}</span>
                                  )}
                                </span>
                              </div>

                              {/* Reply Action Menu */}
                              {currentUser && (currentUser.id || currentUser.user_id) && (
                                <div
                                  className="channel-post-reply-menu"
                                  ref={(el) => dropdownRefs.current[`reply-${reply.id}`] = el}
                                >
                                  <button
                                    className="channel-post-reply-menu-btn"
                                    onClick={(e) => toggleDropdown(`reply-${reply.id}`, e)}
                                    aria-label={t("channelPostComments.actions", "Actions")}
                                  >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                      <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
                                    </svg>
                                  </button>

                                  {openDropdowns[`reply-${reply.id}`] && (
                                    <div className="channel-post-reply-dropdown">
                                      {isCommentOwner(reply) ? (
                                        <>
                                          <button
                                            className="channel-post-reply-dropdown-item"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              startEditReply(reply);
                                            }}
                                          >
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                            </svg>
                                            <span>{t("channelPostComments.edit", "Edit")}</span>
                                          </button>
                                          <button
                                            className="channel-post-reply-dropdown-item channel-post-reply-dropdown-item--delete"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleDeleteReplyClick(reply.id, comment.id);
                                              closeAllDropdowns();
                                            }}
                                            disabled={deletingReplies[reply.id]}
                                          >
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                              <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                            </svg>
                                            <span>{t("channelPostComments.delete", "Delete")}</span>
                                          </button>
                                        </>
                                      ) : (
                                        <button
                                          className="channel-post-reply-dropdown-item"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleReportClick(reply);
                                          }}
                                          disabled={reply.has_reported}
                                        >
                                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"></path>
                                            <line x1="4" y1="22" x2="4" y2="15"></line>
                                          </svg>
                                          <span>
                                            {reply.has_reported
                                              ? t("channelPostComments.alreadyReported", "Already Reported")
                                              : t("channelPostComments.report", "Report")}
                                          </span>
                                        </button>
                                      )}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>

                            {editingReplies[reply.id] ? (
                              <div className="channel-post-reply-edit-form">
                                <textarea
                                  value={editInputs[`reply-${reply.id}`] || ''}
                                  onChange={(e) => setEditInputs(prev => ({ ...prev, [`reply-${reply.id}`]: e.target.value }))}
                                  placeholder={t("channelPostComments.editPlaceholder", "Edit your reply...")}
                                  className="channel-post-reply-edit-input"
                                  dir={isArabicText(editInputs[`reply-${reply.id}`] || '') ? 'rtl' : 'ltr'}
                                  rows={2}
                                />
                                <div className="channel-post-reply-edit-actions">
                                  <button
                                    className="channel-post-reply-edit-btn channel-post-reply-edit-btn--cancel"
                                    onClick={() => cancelEdit(`reply-${reply.id}`)}
                                  >
                                    {t("channelPostComments.cancel", "Cancel")}
                                  </button>
                                  <button
                                    className="channel-post-reply-edit-btn channel-post-reply-edit-btn--save"
                                    onClick={() => saveEdit(`reply-${reply.id}`)}
                                    disabled={!editInputs[`reply-${reply.id}`]?.trim()}
                                  >
                                    {t("channelPostComments.save", "Save")}
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div
                                className="channel-post-reply-text"
                                dir={isArabicText(reply.content) ? 'rtl' : 'ltr'}
                              >
                                {formatContent(reply.content)}
                              </div>
                            )}

                            {/* Reply Reactions */}
                            <div className="channel-post-reply-actions">
                              <div className="channel-post-reply-actions-wrapper">
                                <div
                                  ref={(el) => replyContainerRefs.current[reply.id] = el}
                                  className="channel-post-reply-reaction-container"
                                  onMouseEnter={() => handleReplyMouseEnter(reply.id)}
                                  onMouseLeave={(e) => handleReplyMouseLeave(reply.id, e)}
                                  style={{ position: 'relative' }}
                                >
                                  <button
                                    className={`channel-post-reply-like-btn ${reply.user_reaction_type ? 'has-reaction' : ''}`}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      if (!showReplyReactionPicker[reply.id]) {
                                        handleReplyMouseEnter(reply.id);
                                      }
                                    }}
                                  >
                                    {reply.user_reaction_type && reactionTypeToEmoji[reply.user_reaction_type] ? (
                                      (() => {
                                        const emoji = reactionTypeToEmoji[reply.user_reaction_type];
                                        const isImagePath = typeof emoji === 'string' && (emoji.startsWith('/') || emoji.includes('.png') || emoji.includes('.jpg') || emoji.includes('.svg'));
                                        return isImagePath ? (
                                          <img
                                            src={emoji}
                                            alt={reply.user_reaction_type}
                                            style={{ width: "32px", height: "32px", objectFit: 'contain', display: 'inline-block' }}
                                          />
                                        ) : (
                                          <span style={{ fontSize: '16px' }}>{emoji}</span>
                                        );
                                      })()
                                    ) : (
                                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
                                      </svg>
                                    )}
                                    <span>{reply.total_reactions || reply.likes || 0}</span>
                                  </button>

                                  {showReplyReactionPicker[reply.id] && (
                                    <div
                                      className="channel-post-reaction-picker"
                                      style={{
                                        position: 'fixed',
                                        top: `${replyPickerPositions[reply.id]?.top ?? 0}px`,
                                        left: `${replyPickerPositions[reply.id]?.left ?? 0}px`,
                                        bottom: 'auto',
                                      }}
                                      onMouseEnter={() => handleReplyMouseEnter(reply.id)}
                                      onMouseLeave={(e) => handleReplyMouseLeave(reply.id, e)}
                                    >
                                      {reactions.map((reaction, index) => (
                                        <button
                                          key={index}
                                          className="channel-post-reaction-emoji"
                                          onMouseDown={(e) => {
                                            e.stopPropagation();
                                            e.preventDefault();
                                            handleReplyReaction(reply.id, reaction, e, comment.id);
                                          }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            e.preventDefault();
                                          }}
                                          disabled={reactingReplies[reply.id]}
                                        >
                                          {(() => {
                                            const isImagePath = typeof reaction === 'string' && (reaction.startsWith('/') || reaction.includes('.png') || reaction.includes('.jpg') || reaction.includes('.svg'));
                                            return isImagePath ? (
                                              <img
                                                src={reaction}
                                                alt={reactionTypes[index]}
                                                style={{ width: "72px", height: "72px", objectFit: 'contain', display: 'inline-block' }}
                                              />
                                            ) : (
                                              <span>{reaction}</span>
                                            );
                                          })()}
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                </div>
                                {renderReactionBreakdown(reply.reaction_counts)}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    )}

                    {/* Load more replies (pagination) */}
                    {repliesHasMore[comment.id] && (
                      <button
                        type="button"
                        className="channel-post-replies-load-more"
                        onClick={() => handleLoadMoreReplies(comment.id)}
                        disabled={loadingReplies[comment.id]}
                      >
                        {loadingReplies[comment.id]
                          ? t("channelPostComments.loadingMore", "Loading...")
                          : t("channelPostComments.showMoreReplies", "Show more replies")}
                      </button>
                    )}
                  </div>
                )}

                {/* Reply Input Form - Show when replies are visible or reply button clicked */}
                {(showReplies[comment.id] || showReplyInput[comment.id]) && currentUser && (currentUser.id || currentUser.user_id) && (
                  <form
                    onSubmit={(e) => handleReplySubmit(comment.id, e)}
                    className="channel-post-reply-form"
                  >
                    <img
                      src={commentAsClinic && isClinicOwner
                        ? clinicContext.logo || getUserPlaceholderImage()
                        : getProfileImageUrl() || getUserPlaceholderImage(currentUser)}
                      alt="Profile"
                      className="channel-post-reply-form-avatar"
                      onError={(e) => {
                        e.target.src = commentAsClinic && isClinicOwner
                          ? getUserPlaceholderImage()
                          : getUserPlaceholderImage(comment.author);
                      }}
                    />
                    <div className="channel-post-reply-form-input-container">
                      <input
                        type="text"
                        value={replyInputs[comment.id] || ''}
                        onChange={(e) => setReplyInputs(prev => ({ ...prev, [comment.id]: e.target.value }))}
                        placeholder={t(
                          "channelPostComments.replyPlaceholder",
                          "Write a reply..."
                        )}
                        className="channel-post-reply-form-input"
                        dir={isArabicText(replyInputs[comment.id] || '') ? 'rtl' : 'ltr'}
                      />
                      <button
                        type="submit"
                        className="channel-post-reply-form-submit"
                        disabled={!replyInputs[comment.id]?.trim() || submittingReplies[comment.id]}
                      >
                        {submittingReplies[comment.id]
                          ? t("channelPostComments.posting", "Posting...")
                          : t("channelPostComments.post", "Post")}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
            ))}
            {/* Infinite scroll sentinel */}
            <div ref={loadMoreRef} className="channel-post-comments-sentinel">
              {isLoadingMore && (
                <div className="channel-post-comments-loading-more">
                  <div className="channel-post-comments-spinner-small"></div>
                  <span>{t("channelPostComments.loadingMore", "Loading more...")}</span>
                </div>
              )}
              {!hasMore && comments.length > 20 && (
                <div className="channel-post-comments-end">
                  {t("channelPostComments.allLoaded", "All comments loaded")}
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="channel-post-comments-empty">
            <p>
              {t(
                "channelPostComments.empty",
                "No comments yet. Be the first to comment!"
              )}
            </p>
          </div>
        )}
      </div>

      {isClinicOwner && (
        <button
          type="button"
          className={`channel-post-comment-as-toggle ${commentAsClinic ? "channel-post-comment-as-toggle--clinic" : ""}`}
          onClick={() => setCommentAsClinic(prev => !prev)}
        >
          {commentAsClinic ? (
            <>
              <img
                src={clinicContext.logo || getUserPlaceholderImage()}
                alt=""
                className="channel-post-comment-as-toggle-logo"
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
              {t("comment.commentAsClinic", {
                name: clinicContext.name_en || clinicContext.name_ar,
                defaultValue: "Comment as {{name}}"
              })}
            </>
          ) : (
            t("comment.commentAsYourself", "Comment as yourself")
          )}
        </button>
      )}

      <form onSubmit={handleSubmitComment} className="channel-post-comment-form">
        <img
          src={commentAsClinic && isClinicOwner
            ? clinicContext.logo || getUserPlaceholderImage()
            : getProfileImageUrl() || getUserPlaceholderImage(currentUser)}
          alt="Profile"
          className="channel-post-comment-form-avatar"
          onError={(e) => {
            e.target.src = getUserPlaceholderImage(currentUser);
          }}
        />
        <div className="channel-post-comment-form-input-container">
          <input
            type="text"
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            placeholder={t(
              "channelPostComments.placeholder",
              "Write a comment..."
            )}
            className="channel-post-comment-form-input"
            dir={isArabicText(commentText) ? 'rtl' : 'ltr'}
          />
          <button
            type="submit"
            className="channel-post-comment-form-submit"
            disabled={!commentText.trim() || isSubmitting}
          >
            {isSubmitting
              ? t("channelPostComments.posting", "Posting...")
              : t("channelPostComments.post", "Post")}
          </button>
        </div>
      </form>

      {/* Delete Comment Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteCommentModal.isOpen}
        title={t("channelPostComments.deleteTitle", "Delete Comment")}
        message={t(
          "channelPostComments.confirmDeleteComment",
          "Are you sure you want to delete this comment?"
        )}
        confirmLabel={t("channelPostComments.delete", "Delete")}
        cancelLabel={t("common.cancel", "Cancel")}
        onConfirm={handleDeleteCommentConfirm}
        onCancel={handleDeleteCommentCancel}
        showWarningIcon={true}
      />

      {/* Delete Reply Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteReplyModal.isOpen}
        title={t("channelPostComments.deleteTitle", "Delete Reply")}
        message={t(
          "channelPostComments.confirmDeleteReply",
          "Are you sure you want to delete this reply?"
        )}
        confirmLabel={t("channelPostComments.delete", "Delete")}
        cancelLabel={t("common.cancel", "Cancel")}
        onConfirm={handleDeleteReplyConfirm}
        onCancel={handleDeleteReplyCancel}
        showWarningIcon={true}
      />

      {/* Error Modal */}
      <ConfirmModal
        isOpen={errorModal.isOpen}
        title={t("channelPostComments.errorTitle", "Error")}
        message={errorModal.message}
        confirmLabel={t("common.ok", "OK")}
        onConfirm={() => setErrorModal({ isOpen: false, message: '' })}
        onCancel={() => setErrorModal({ isOpen: false, message: '' })}
        showWarningIcon={true}
      />

      {/* Report Modal */}
      <ReportModal
        isOpen={showReportModal}
        onClose={() => {
          setShowReportModal(false);
          setReportTarget(null);
        }}
        reportableType={reportTarget?.type || 'App\\Models\\PostComment'}
        reportableId={reportTarget?.id}
        post={null}
        onSuccess={handleReportSuccess}
      />
    </div>
  );
};

export default ChannelPostComments;

